// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { PlanActions, PlanDrivers, PlanFile, PlanRequestedChange } from '../types';

/**
 * Plans filed before the drivers/actions narration (plan_version v1) carry a
 * `summary` and per-phase `equipment_changes` instead. The detail page renders one
 * layout for every plan, so those older payloads are mapped onto the same
 * drivers/actions shape here, from what they do have:
 *
 * - drivers: the legacy summary becomes the narrative; there are no driving
 *   factors or load impacts to list, and none are invented.
 * - actions: every phase's equipment change becomes one requested change.
 *
 * A plan that already carries its own narration is returned untouched, and a
 * part the older payload has nothing for stays absent (the tab says so).
 */
export function withStandardNarration(planFile: PlanFile): PlanFile {
  const summary = planFile.summary?.trim() ?? '';

  const drivers: PlanDrivers | undefined =
    planFile.drivers ??
    (summary ? { narrative: summary, factors: [], load_impacts: [] } : undefined);

  let actions: PlanActions | undefined = planFile.actions;
  if (!actions) {
    const requested: PlanRequestedChange[] = (planFile.phases ?? []).flatMap(phase =>
      (phase.equipment_changes ?? []).map(change => ({
        equipment: change.equipment,
        parameter: change.parameter,
        what_were_asking: `${phase.name} (${phase.time_window.start}–${phase.time_window.end}): move ${change.parameter} from ${change.from_value} to ${change.to_value}${change.unit ? ` ${change.unit}` : ''}.`,
        baseline_value: change.from_value,
        recommended_value: change.to_value,
        unit: change.unit ?? null,
      }))
    );
    if (requested.length > 0) {
      actions = { narrative: summary, requested_changes: requested, not_requested: [] };
    }
  }

  if (drivers === planFile.drivers && actions === planFile.actions) return planFile;
  return { ...planFile, drivers, actions };
}
